import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { getLocalizedString } from "@icarius-localization/strings";
import { CloseIcon } from "@icarius-icons";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Typography,
  InputAdornment,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
} from "@material-ui/core";
import DialogTitleDivider from "@icarius-common/dialogTitleDivider";
import ButtonDialogAction from "@icarius-common/buttonDialogAction";
import PaperDraggable from "@icarius-common/paperDraggable";
import DialogTransition from "@icarius-common/dialogTransition";
import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import Loader from "@icarius-common/loader";
import { getYesNoArray } from "@icarius-utils/constants";
import DynamicInput from "@icarius-common/dynamicInputs";
import FileInput from "@icarius-common/fileUpload/fileInput";
import FileList from "@icarius-common/fileUpload/fileList";
import { getFileExtension } from "@icarius-utils/fileUpload";
import { Alert, Autocomplete } from "@material-ui/lab";
import { GalleryDialog } from "@icarius-common/galleryDialog";
import GalleryButtonAndDisplay from "@icarius-common/galleryDialog/components/galleryButtonAndDisplay";
import { IMAGES_ENDPOINT } from "@icarius-connection/endpoints";
import Thumbnail from "@icarius-pages/myTraining/components/trainingSummary/thumbnail";
import useHandleForm from "./useHandleForm";
import TextFieldMultiline from "@icarius-common/textFieldMultiline";
import CustomNumberFormat from '@icarius-common/dynamicInputs/customNumberFormat';

const gridStyle = { padding: 10 };

const CourseFormDialog = (props) => {

  const {
    open,
    readOnly,
    data,
    userFieldsDefinition,
    submitCallback,
    handleClose,
    invalidFiles,
    handleClearInvalidFiles,
    isLoading,
  } = props;

  const dispatch = useDispatch();

  const openValidationError = () => {
    dispatch(openSnackbarAction({ msg: getLocalizedString("invalidData"), severity: "error", duration: 10000 }));
  }

  const {
    isCreate,
    formData,
    previewFileComesFromBackend,
    setFormValue,
    submit,
  } = useHandleForm(data, userFieldsDefinition, submitCallback, openValidationError);

  const [galleryDialogIsOpen, setGalleryDialogIsOpen] = useState(false);

  return (
    <>
      <Dialog
        open={open}
        TransitionComponent={DialogTransition}
        PaperComponent={PaperDraggable}
        maxWidth={"md"}
        fullWidth={true}
      >
        <div className={"dialog-container"}>
          <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
            {isCreate ? getLocalizedString("createCourse") : getLocalizedString("updateCourse")}
            <DialogTitleDivider />
          </DialogTitle>
          <CloseIcon className={"dialog-close-icon icon"} onClick={handleClose} />
          <DialogContent>
            <Loader open={isLoading} />
            <Grid container item xs={12}>
              {
                readOnly &&
                <div style={{ width: "100%", padding: 14, paddingTop: 0 }}>
                  <Alert variant="filled" severity="warning">
                    {getLocalizedString("courseInUseUpdate")}
                  </Alert>
                </div>
              }
              <Grid container item alignItems="center" xs={12} style={gridStyle}>
                <TextField
                  disabled={readOnly}
                  required
                  fullWidth
                  margin={"none"}
                  label={getLocalizedString("name")}
                  value={formData.name}
                  onChange={(e) => setFormValue(e.target.value, "name")}
                />
              </Grid>
              <Grid container item alignItems="center" xs={12} sm={6} md={3} style={gridStyle}>
                <FormControl style={{ width: "100%" }}>
                  <InputLabel required id={`label-published`}>{getLocalizedString("published")}</InputLabel>
                  <Select
                    disabled={readOnly}
                    value={formData.published ? "Y" : "N"}
                    labelId={`label-published`}
                    id={`select-published`}
                    onChange={(e) => setFormValue(e.target.value === "Y", "published")}
                    margin={"none"}
                  >
                    {
                      getYesNoArray().map((item) => (
                        <MenuItem
                          className={"whiteText"}
                          key={item.key}
                          value={item.key}
                        >
                          {item.value}
                        </MenuItem>
                      ))
                    }
                  </Select>
                </FormControl>
              </Grid>
              <Grid container item alignItems="center" xs={12} sm={6} md={6} style={gridStyle}>
                <TextField
                  disabled={readOnly}
                  required
                  fullWidth
                  margin={"none"}
                  label={getLocalizedString("themeArea")}
                  value={formData.area}
                  onChange={(e) => setFormValue(e.target.value, "area")}
                />
              </Grid>
              <Grid container item alignItems="center" xs={12} sm={6} md={3} style={gridStyle}>
                <TextField
                  disabled={readOnly}
                  fullWidth
                  margin={"none"}
                  label={getLocalizedString("recognitionPoints")}
                  InputProps={{ inputComponent: CustomNumberFormat }}
                  value={formData.points}
                  onChange={(e) => setFormValue(e.target.value, "points")}
                  inputProps={{ min: 0, max: 999, decimals: 0, isAllowed: (v) => v.floatValue === undefined || v.floatValue <= 999 }}
                />
              </Grid>
              <Grid container item alignItems="center" xs={12} style={{ ...gridStyle, paddingTop: 20 }}>
                <TextFieldMultiline
                  disabled={readOnly}
                  required
                  fullWidth
                  label={getLocalizedString("description")}
                  rows={4}
                  margin={"none"}
                  value={formData.description}
                  onChange={(e) => setFormValue(e.target.value, "description")}
                  inputProps={{ spellCheck: 'false' }}
                />
              </Grid>
              <Grid container item alignItems="center" xs={12} style={gridStyle}>
                <Autocomplete
                  disabled={readOnly}
                  fullWidth
                  multiple
                  freeSolo
                  required
                  options={[]}
                  value={formData.themes}
                  onChange={(e) => setFormValue(e.target.value, "themes")}
                  renderInput={(params) => {
                    return (
                      <TextField
                        {...params}
                        required
                        label={getLocalizedString("themes")}
                        helperText={getLocalizedString("pressEnterToAdd")}
                      />
                    )
                  }}
                  renderTags={(value, props) => {
                    return value.map((option, index) => {
                      return (
                        <Chip
                          {...props({ index })}
                          size="small"
                          color="primary"
                          label={option}
                          onDelete={() => setFormValue(option, "themes")}
                        />
                      )
                    })
                  }}
                  closeIcon={null}
                />
              </Grid>
              {
                !(readOnly && !formData.previewFile?.[0] && !formData.previewFromGallery) &&
                <>
                  <Grid container item direction="column" alignItems="center" justify="center" xs={12} style={{ ...gridStyle, paddingTop: 30 }}>
                    <Typography className="whiteText" variant="h6">
                      {getLocalizedString("thumbnail")}
                    </Typography>
                    <Typography className="whiteText" variant="caption">
                      {getLocalizedString("trainingThumbnailExplanation")}
                    </Typography>
                  </Grid>
                  <Grid container item xs={12} spacing={1}>
                    <Card style={{ width: "100%", padding: 16 }}>
                      <Grid container item alignItems="flex-start" xs={12}>
                        <Grid container item xs={12} sm={6} style={gridStyle}>
                          <FileInput
                            disabled={readOnly}
                            label={getLocalizedString("uploadThumbnailFile")}
                            acceptedFormats={[".jpeg", ".jpg", ".bpm", ".png", ".gif", ".mp4", ".webm", ".mov", ".mkv"]}
                            handleChange={(files) => setFormValue(files, "previewFile")}
                            id={"previewFile"}
                            preSubmitCallback={handleClearInvalidFiles}
                          />
                          <FileList
                            disabled={readOnly}
                            files={formData.previewFile}
                            filesWithError={invalidFiles}
                            handleClick={() => setFormValue(null, "previewFile")}
                          />
                          {
                            formData.previewFile?.[0] &&
                            <div style={{ width: 220, paddingTop: 10 }}>
                              <Thumbnail
                                isVideo={["mp4", "webm", "mov", "mkv"].includes(getFileExtension(formData.previewFile[0]?.name || ""))}
                                path={
                                  (() => {
                                    if (previewFileComesFromBackend) return IMAGES_ENDPOINT + data?.previewFile?.file;
                                    return URL.createObjectURL(formData.previewFile[0])
                                  })()
                                }
                              />
                            </div>
                          }
                        </Grid>
                        <Grid container item xs={12} sm={6} style={gridStyle}>
                          <GalleryButtonAndDisplay
                            disabled={readOnly}
                            image={formData.previewFromGallery}
                            handleOpenGallery={() => setGalleryDialogIsOpen(true)}
                          />
                        </Grid>
                      </Grid>
                    </Card>
                  </Grid>
                </>
              }
              <Grid container item alignItems="center" justify="center" xs={12} style={{ ...gridStyle, paddingTop: 30 }}>
                <Typography className="whiteText" variant="h6">
                  {getLocalizedString("finalizationCertificates")}
                </Typography>
              </Grid>
              <Grid container item xs={12} spacing={1}>
                <Grid container item alignItems="flex-start" xs={12} sm={6}>
                  <Card style={{ width: "100%", padding: 16 }}>
                    <Grid container item alignItems="center" xs={12} style={gridStyle}>
                      <FormControl style={{ width: "100%" }}>
                        <InputLabel id={`label-hasAssistanceCertificate`}>{getLocalizedString("hasAssistanceCertificate")}</InputLabel>
                        <Select
                          disabled={readOnly}
                          value={formData.hasAssistanceCertificate ? "Y" : "N"}
                          labelId={`label-hasAssistanceCertificate`}
                          id={`select-hasAssistanceCertificate`}
                          onChange={(e) => setFormValue(e.target.value === "Y", "hasAssistanceCertificate")}
                          margin={"none"}
                        >
                          {
                            getYesNoArray().map((item) => (
                              <MenuItem
                                className={"whiteText"}
                                key={item.key}
                                value={item.key}
                              >
                                {item.value}
                              </MenuItem>
                            ))
                          }
                        </Select>
                      </FormControl>
                    </Grid>
                    {
                      formData.hasAssistanceCertificate &&
                      <>
                        <Grid container item alignItems="center" xs={12} style={gridStyle}>
                          <TextField
                            disabled={readOnly}
                            required
                            fullWidth
                            margin={"none"}
                            label={getLocalizedString("assistancePercentageRequired")}
                            value={formData.assistancePercentageRequired}
                            onChange={(e) => setFormValue(e.target.value, "assistancePercentageRequired")}
                            inputProps={{ min: 1, max: 100, decimals: 2, isAllowed: (v) => v.floatValue === undefined || v.floatValue <= 100 }}
                            helperText={"Min.: 0. Max.: 100"}
                            InputProps={{
                              inputComponent: CustomNumberFormat,
                              endAdornment: (
                                <InputAdornment position="end">
                                  <Typography className="whiteText">
                                    {"%"}
                                  </Typography>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                        <Grid container item alignItems="center" xs={12} style={gridStyle}>
                          <FileInput
                            disabled={readOnly}
                            required
                            acceptedFormats={[".html"]}
                            handleChange={(files) => setFormValue(files, "assistanceCertificateFile")}
                            id={"assistanceCertificateFile"}
                            preSubmitCallback={handleClearInvalidFiles}
                          />
                          <FileList
                            disabled={readOnly}
                            files={formData.assistanceCertificateFile}
                            filesWithError={invalidFiles}
                            handleClick={() => setFormValue(null, "assistanceCertificateFile")}
                          />
                        </Grid>
                      </>
                    }
                  </Card>
                </Grid>
                <Grid container item alignItems="flex-start" xs={12} sm={6}>
                  <Card style={{ width: "100%", padding: 16 }}>
                    <Grid container item alignItems="center" xs={12} style={gridStyle}>
                      <FormControl style={{ width: "100%" }}>
                        <InputLabel id={`label-hasKnowledgeCertificate`}>{getLocalizedString("hasKnowledgeCertificate")}</InputLabel>
                        <Select
                          disabled={readOnly}
                          value={formData.hasKnowledgeCertificate ? "Y" : "N"}
                          labelId={`label-hasKnowledgeCertificate`}
                          id={`select-hasKnowledgeCertificate`}
                          onChange={(e) => setFormValue(e.target.value === "Y", "hasKnowledgeCertificate")}
                          margin={"none"}
                        >
                          {
                            getYesNoArray().map((item) => (
                              <MenuItem
                                className={"whiteText"}
                                key={item.key}
                                value={item.key}
                              >
                                {item.value}
                              </MenuItem>
                            ))
                          }
                        </Select>
                      </FormControl>
                    </Grid>
                    {
                      formData.hasKnowledgeCertificate &&
                      <>
                        <Grid container item alignItems="center" xs={12}>
                          <Grid container item alignItems="center" sm={12} md={6} style={gridStyle}>
                            <TextField
                              disabled={readOnly}
                              required
                              fullWidth
                              margin={"none"}
                              label={getLocalizedString("knowledgeCertificateMinScore")}
                              InputProps={{ inputComponent: CustomNumberFormat }}
                              value={formData.knowledgeCertificateMinScore}
                              onChange={(e) => setFormValue(e.target.value, "knowledgeCertificateMinScore")}
                              inputProps={{ min: 0, max: formData.knowledgeCertificateMaxScore || 100, decimals: 2, isAllowed: (v) => v.floatValue === undefined || v.floatValue <= (formData.knowledgeCertificateMaxScore || 100) }}
                              helperText={`Min.: 0. Max.: ${formData.knowledgeCertificateMaxScore || 100}`}
                            />
                          </Grid>
                          <Grid container item alignItems="center" sm={12} md={6} style={gridStyle}>
                            <TextField
                              disabled={readOnly}
                              required
                              fullWidth
                              margin={"none"}
                              label={getLocalizedString("knowledgeCertificateMaxScore")}
                              InputProps={{ inputComponent: CustomNumberFormat }}
                              value={formData.knowledgeCertificateMaxScore}
                              onChange={(e) => setFormValue(e.target.value, "knowledgeCertificateMaxScore")}
                              inputProps={{ min: formData.knowledgeCertificateMinScore || 0, max: 100, decimals: 2, isAllowed: (v) => v.floatValue === undefined || v.floatValue <= 100 }}
                              helperText={`Min.: ${formData.knowledgeCertificateMinScore || 0}. Max.: 100`}
                            />
                          </Grid>
                          <Grid container item alignItems="center" xs={12} style={gridStyle}>
                            <TextField
                              disabled={readOnly}
                              required
                              fullWidth
                              margin={"none"}
                              label={getLocalizedString("knowledgeCertificateMinScoreToPass")}
                              InputProps={{ inputComponent: CustomNumberFormat }}
                              value={formData.knowledgeCertificateMinScoreToPass}
                              onChange={(e) => setFormValue(e.target.value, "knowledgeCertificateMinScoreToPass")}
                              inputProps={{ min: formData.knowledgeCertificateMinScore || 0, max: formData.knowledgeCertificateMaxScore || 100, decimals: 2, isAllowed: (v) => v.floatValue === undefined || v.floatValue <= (formData.knowledgeCertificateMaxScore || 100) }}
                              helperText={`Min.: ${formData.knowledgeCertificateMinScore || 0}. Max.: ${formData.knowledgeCertificateMaxScore || 100}`}
                            />
                          </Grid>
                        </Grid>
                        <Grid container item alignItems="center" xs={12} style={gridStyle}>
                          <FileInput
                            disabled={readOnly}
                            required
                            acceptedFormats={[".html"]}
                            handleChange={(files) => setFormValue(files, "knowledgeCertificateFile")}
                            id={"knowledgeCertificateFile"}
                            preSubmitCallback={handleClearInvalidFiles}
                          />
                          <FileList
                            disabled={readOnly}
                            files={formData.knowledgeCertificateFile}
                            filesWithError={invalidFiles}
                            handleClick={() => setFormValue(null, "knowledgeCertificateFile")}
                          />
                        </Grid>
                      </>
                    }
                  </Card>
                </Grid>
              </Grid>
              {
                Boolean(userFieldsDefinition.length) &&
                <>
                  <Grid container item alignItems="center" justify="center" xs={12} style={{ ...gridStyle, paddingTop: 30 }}>
                    <Typography className="whiteText" variant="h6">
                      {getLocalizedString("userFields")}
                    </Typography>
                  </Grid>
                  <Card style={{ width: "100%", padding: 16, marginTop: 15 }}>
                    <Grid container item alignItems="center" xs={12}>
                      {
                        userFieldsDefinition?.map((fieldConfig) => {
                          return (
                            <Grid
                              container item alignItems="center" xs={12} sm={6} style={gridStyle}
                              key={fieldConfig.name}
                            >
                              <DynamicInput
                                fieldConfig={readOnly ? null : fieldConfig}
                                label={fieldConfig.name}
                                value={formData[fieldConfig.name]}
                                required={fieldConfig.required}
                                handleChange={setFormValue}
                              />
                            </Grid>
                          )
                        })
                      }
                    </Grid>
                  </Card>
                </>
              }
            </Grid>
          </DialogContent>
          <DialogActions>
            {
              !readOnly &&
              <>
                <ButtonDialogAction onClick={handleClose} text={getLocalizedString("cancel")} />
                <ButtonDialogAction onClick={() => submit(formData)} isAccept text={getLocalizedString("accept")} />
              </>
            }
            {readOnly && <ButtonDialogAction onClick={handleClose} isAccept text={getLocalizedString("close")} />}
          </DialogActions>
        </div>
      </Dialog >
      {
        galleryDialogIsOpen &&
        <GalleryDialog
          open={galleryDialogIsOpen}
          initialValue={formData.previewFromGallery}
          type={"training"}
          handleSubmit={(image) => setFormValue(image, "previewFromGallery")}
          handleClose={() => setGalleryDialogIsOpen(false)}
        />
      }
    </>
  );
}

export default CourseFormDialog;
